import React from "react";
import { createPortal } from "react-dom";
import { selectors as notificationSelectors, actions as notificationActions } from "reducers/notification";
import { connect } from "react-redux";
import { arrayOf, bool, element, func, number, string, shape } from "prop-types";
import NotificationSystem from "react-notification-system";
import { resizableRoute } from "pages/_components/Resizable";

/*
Por customizacion ver, ver: https://github.com/igorprado/react-notification-system
*/
class Notification extends React.Component {
    _lastDisplayedKey = null;

    _removeTimer = null;

    static propTypes = {
        scopeToShow: string.isRequired, // what scope this notification component must listen
        message: string.isRequired,
        scopes: arrayOf(element).isRequired,
        dispatch: func.isRequired,
        level: string.isRequired,
        autoDismiss: bool.isRequired,
        errors: shape({}),
        metadata: shape({
            idForm: string.isRequired,
            version: number,
            enabled: bool,
            category: string.isRequired,
            type: string.isRequired,
            adminOption: string.isRequired,
            idActivity: string.isRequired,
            fieldList: arrayOf(
                shape({
                    idField: string.isRequired,
                    labelMap: shape({}).isRequired,
                    requiredErrorMap: shape({}).isRequired,
                }),
            ).isRequired,
        }).isRequired,
        currentLang: string.isRequired,
        title: string,
        notificationClassname: string,
        dismissible: string.isRequired,
        /** RequestCredin Android: portal avoids PageTransition transform clipping. */
        renderInBodyPortal: bool,
        /** RequestCredin Android: keep Redux message until toast lifetime ends (survives remount). */
        retainReduxUntilDismiss: bool,
        /** RequestCredin Android: skip duplicate show when layout fires twice. */
        preventDuplicateDisplay: bool,
    };

    static defaultProps = {
        errors: {},
        title: null,
        notificationClassname: null,
        renderInBodyPortal: false,
        retainReduxUntilDismiss: false,
        preventDuplicateDisplay: false,
    };

    state = {
        notificationSystem: null,
    };

    componentDidMount() {
        // eslint-disable-next-line react/no-string-refs
        const { notificationSystem } = this.refs;

        this.setState({ notificationSystem }, () => {
            this.runAfterLayout(this.tryShowPendingNotification);
        });
    }

    componentDidUpdate(prevProps) {
        const { message, scopes, scopeToShow, level } = this.props;
        const { notificationSystem } = this.state;

        if (!message || !scopes || scopes.indexOf(scopeToShow) === -1) {
            return;
        }

        const notificationChanged =
            message !== prevProps.message ||
            level !== prevProps.level ||
            scopes.join(",") !== (prevProps.scopes || []).join(",");

        if (!notificationChanged) {
            return;
        }

        if (notificationSystem) {
            this.runAfterLayout(this.tryShowPendingNotification);
        }
    }

    componentWillUnmount() {
        if (this._removeTimer) {
            clearTimeout(this._removeTimer);
            this._removeTimer = null;
        }
    }

    /**
     * Builds a stable key for the current Redux notification payload.
     */
    getNotificationKey = () => {
        const { message, level, scopes, scopeToShow } = this.props;

        return `${scopeToShow}|${level}|${message}|${(scopes || []).join(",")}`;
    };

    /**
     * Schedules work after the browser has applied layout (useLayoutEffect equivalent for class components).
     */
    runAfterLayout = (callback) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(callback);
        });
    };

    /**
     * Shows a Redux pending notification when this listener's scope matches and the UI is ready.
     */
    tryShowPendingNotification = () => {
        const { message, scopes, scopeToShow } = this.props;
        const { notificationSystem } = this.state;

        if (!notificationSystem || !message || !scopes || scopes.indexOf(scopeToShow) === -1) {
            return;
        }

        this.addNotification();
    };

    addNotification = () => {
        const { notificationSystem } = this.state;
        const {
            dispatch,
            message,
            level,
            errors,
            metadata,
            currentLang,
            title,
            autoDismiss,
            dismissible,
            preventDuplicateDisplay,
            retainReduxUntilDismiss,
        } = this.props;

        if (!notificationSystem) {
            return;
        }

        const notificationKey = this.getNotificationKey();

        if (preventDuplicateDisplay && this._lastDisplayedKey === notificationKey) {
            return;
        }

        if (preventDuplicateDisplay) {
            this._lastDisplayedKey = notificationKey;
        }

        let allFormFieldsInfo;
        let errorFieldsKeys;
        let errorFieldsInfo;

        if (metadata) {
            allFormFieldsInfo = metadata.fieldList;
            errorFieldsKeys = Object.keys(errors);
            errorFieldsInfo = allFormFieldsInfo.filter((field) => errorFieldsKeys.includes(field.idField));
        }

        const autoDismissSeconds = autoDismiss ? 5 : 0;
        const finalTitle = title || (level === "error" ? "Error. " : "Atención. ");
        const finalMessage = (
            <div>
                <span style={{ fontWeight: 700 }}>{`${finalTitle}`}</span>
                <span>{` ${message}`}</span>
            </div>
        );
        notificationSystem.clearNotifications();
        if (dismissible !== null) {
            if (level !== "success") {
                notificationSystem.addNotification({
                    message: finalMessage,
                    level,
                    position: "tc",
                    autoDismiss: autoDismissSeconds,
                    dismissible: true,
                    children: (
                        <div className="visually-hidden">
                            {errorFieldsInfo &&
                                errorFieldsInfo.map((field) => {
                                    const label = field.labelMap[currentLang];
                                    const error = field.requiredErrorMap[currentLang];

                                    return <div key={field.idField}>{`${label} : ${error}`}</div>;
                                })}
                        </div>
                    ),
                });
            } else {
                notificationSystem.addNotification({
                    message,
                    level,
                    position: "tc",
                    autoDismiss: autoDismissSeconds,
                    // dismissible: button,
                    dismissible: "button",
                    children: (
                        <div className="visually-hidden">
                            {errorFieldsInfo &&
                                errorFieldsInfo.map((field) => {
                                    const label = field.labelMap[currentLang];
                                    const error = field.requiredErrorMap[currentLang];

                                    return <div key={field.idField}>{`${label} : ${error}`}</div>;
                                })}
                        </div>
                    ),
                });
            }
        } else if (level !== "success") {
            notificationSystem.addNotification({
                message: finalMessage,
                level,
                position: "tc",
                autoDismiss: autoDismissSeconds,
                dismissible,
                children: (
                    <div className="visually-hidden">
                        {errorFieldsInfo &&
                            errorFieldsInfo.map((field) => {
                                const label = field.labelMap[currentLang];
                                const error = field.requiredErrorMap[currentLang];

                                return <div key={field.idField}>{`${label} : ${error}`}</div>;
                            })}
                    </div>
                ),
            });
        } else {
            notificationSystem.addNotification({
                message,
                level,
                position: "tc",
                autoDismiss: autoDismissSeconds,
                dismissible,
                children: (
                    <div className="visually-hidden">
                        {errorFieldsInfo &&
                            errorFieldsInfo.map((field) => {
                                const label = field.labelMap[currentLang];
                                const error = field.requiredErrorMap[currentLang];

                                return <div key={field.idField}>{`${label} : ${error}`}</div>;
                            })}
                    </div>
                ),
            });
        }

        if (retainReduxUntilDismiss) {
            if (this._removeTimer) {
                clearTimeout(this._removeTimer);
            }

            const reduxClearDelayMs = autoDismissSeconds > 0 ? autoDismissSeconds * 1000 + 500 : 500;

            this._removeTimer = setTimeout(() => {
                dispatch(notificationActions.removeNotification());
                if (this._lastDisplayedKey === notificationKey) {
                    this._lastDisplayedKey = null;
                }
                this._removeTimer = null;
            }, reduxClearDelayMs);
        } else {
            dispatch(notificationActions.removeNotification());
        }
    };

    render() {
        const { notificationClassname, renderInBodyPortal } = this.props;
        // ignoring warning because the only way to remove all the styles from the component is to pass style as false
        const content = (
            <div aria-live="polite" className={notificationClassname}>
                <NotificationSystem
                    // eslint-disable-next-line react/no-string-refs
                    ref="notificationSystem"
                    // eslint-disable-next-line react/style-prop-object
                    style={false}
                />
            </div>
        );

        if (renderInBodyPortal && typeof document !== "undefined" && document.body) {
            return createPortal(content, document.body);
        }

        return content;
    }
}

const mapStateToProps = (state) => ({
    message: notificationSelectors.getMessage(state),
    level: notificationSelectors.getLevel(state),
    scopes: notificationSelectors.getScopes(state),
    title: notificationSelectors.getTitle(state),
    autoDismiss: notificationSelectors.getAutoDismiss(state),
    dismissible: notificationSelectors.getDismissible(state),
});

export default connect(mapStateToProps)(resizableRoute(Notification));
